<template>
  <nav
    :class="[
      'navbar',
      'is-spaced',
      'is-fixed-top',
      { 'is-transparent': navtransparent },
    ]"
    role="navigation"
    aria-label="main navigation"
  >
    <div class="container">
      <div class="navbar-brand">
        <router-link :to="'/' + $route.params.locale + '/'">
          <img
            v-if="navtransparent"
            alt="Rep.Guide Logo White"
            src="../assets/RepGuideLogoWhite.svg"
            width="300"
            class="is-hidden-mobile"
          />
          <img
            v-else
            alt="Rep.Guide Logo"
            src="../assets/RepGuideLogo.svg"
            width="300"
            class="is-hidden-mobile"
          />
          <img
            alt="Rep.Guide Logo"
            :src="
              navtransparent
                ? require('../assets/RepGuideLogoNoTextWhite.svg')
                : require('../assets/RepGuideLogoNoText.svg')
            "
            width="76"
            class="is-hidden-tablet"
            style="min-width: 76px; max-width: 76px"
          />
        </router-link>

        <div
          v-if="landingpage"
          class="navbar-item ml-auto is-hidden-navbar-expanded"
        >
          <a href="#landingpage-target" class="button is-primary">
            {{ $t("views.discover.email-signup.cta-short") }}
          </a>
        </div>

        <div
          :class="[
            'navbar-item',
            'is-hidden-navbar-expanded',
            { 'ml-auto': !landingpage },
          ]"
        >
          <language-select />
        </div>

        <a
          v-if="!landingpage"
          role="button"
          :class="[
            'navbar-burger burger',
            'ml-0',
            { 'has-text-white': navtransparent },
          ]"
          aria-label="menu"
          @click="isOpen = !isOpen"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </a>
      </div>

      <div :class="['navbar-menu', { 'is-active': isOpen }]">
        <div v-if="!landingpage" class="navbar-start">
          <router-link
            @click="isOpen = false"
            :class="[
              'navbar-item',
              'has-text-weight-bold',
              { 'has-text-white': navtransparent },
            ]"
            :to="'/' + $route.params.locale + '/inhalte'"
          >
            {{ $t("components.navigation.links.features") }}
          </router-link>

          <router-link
            v-if="hasHotlineAccess"
            @click="isOpen = false"
            :class="[
              'navbar-item',
              'has-text-weight-bold',
              { 'has-text-white': navtransparent },
            ]"
            :to="'/' + $route.params.locale + '/hotline'"
          >
            {{ $t("components.navigation.links.hotline") }}
          </router-link>

          <router-link
            @click="isOpen = false"
            :class="[
              'navbar-item',
              'has-text-weight-bold',
              { 'has-text-white': navtransparent },
            ]"
            :to="'/' + $route.params.locale + '/serviceplanner'"
          >
            {{ $t("components.navigation.links.serviceplanner") }}
          </router-link>
        </div>

        <div class="navbar-end">
          <div class="navbar-item">
            <div class="buttons">
              <a
                v-if="landingpage"
                href="#landingpage-target"
                class="button is-primary is-hidden-touch"
              >
                {{ $t("views.discover.email-signup.cta") }}
              </a>

              <a
                v-if="!landingpage"
                class="button is-primary"
                :href="registerLink"
              >
                {{ $t("components.navigation.cta") }}
              </a>
              <a
                v-if="!landingpage"
                @click="isOpen = false"
                class="button"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('components.navigation.login')"
                :href="getLoginUrl($root.$i18n.locale)"
              >
                <span class="mr-2">
                  {{ $t("components.navigation.login") }}
                </span>
                <span class="icon">
                  <fa icon="sign-in-alt" class="icon" />
                </span>
              </a>
              <div class="is-hidden-navbar-collapsed">
                <language-select />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { store } from "../store";
import { getRegisterUrl } from "../register-url.js";
import { generateSearchParams } from "../utm.js";
import LanguageSelect from "./language-select.vue";

export default {
  components: { LanguageSelect },
  data() {
    return {
      isOpen: false,
      topScroll: this.fancy,
    };
  },
  props: {
    fancy: Boolean,
    landingpage: { type: Boolean, default: false },
    offset: { type: Number, default: 50 },
    utm: { type: Object, default: () => {} },
  },
  mounted() {
    this.topScroll = window.pageYOffset < this.offset;
    window.addEventListener("scroll", () => {
      this.topScroll = window.pageYOffset < this.offset;
    });
  },
  computed: {
    hasHotlineAccess() {
      if (!store.selectedPartner?.AppCountry?.country_code) return false;

      return ["DE", "AT", "CH"].includes(
        store.selectedPartner.AppCountry.country_code,
      );
    },
    navtransparent() {
      return this.fancy ? (this.isOpen ? false : this.topScroll) : false;
    },
    registerLink() {
      const UTM = { ...this.utm, utm_content: "main_navigation" };
      return getRegisterUrl(this.$route.params.locale, UTM);
    },
  },
  methods: {
    getLoginUrl(locale) {
      const UTM = { utm_source: "repguide", utm_medium: "marketingseite" };
      return `https://rep.guide/${locale}/login${generateSearchParams(UTM)}`;
    },
  },
};
</script>

<style lang="scss">
.navbar {
  box-shadow: 0 8px 16px rgba(0 0 0 / 0.2);
  &.is-transparent {
    box-shadow: none;
    background: transparent !important;
  }
}
.navbar,
.navbar-item {
  transition: all 0.2s ease;
}

// NOTE: matches $navbar-breakpoint in styles.scss
@media screen and (max-width: 1239.98px) {
  .is-hidden-navbar-collapsed {
    display: none !important;
  }
}
@media screen and (min-width: 1240px) {
  .is-hidden-navbar-expanded {
    display: none !important;
  }
}
// NOTE: bulma's collapsed navbar ends at 1239px, so fractional viewports (e.g. 125% display scaling) fall between both layouts
@media screen and (min-width: 1239px) and (max-width: 1239.98px) {
  .navbar > .container {
    display: block;
  }
  .navbar-brand .navbar-item {
    align-items: center;
    display: flex;
  }
  .navbar-menu.is-active {
    display: block;
    background-color: white;
  }
}

.navbar .navbar-brand a img {
  height: 100%;
  padding: 12px 1rem;
}

.navbar a.navbar-item {
  position: relative;
  transition: none;
  margin-right: 0.25rem;
}
.navbar a.navbar-item.router-link-active {
  color: #e94a10;
  background: #e94a1022;
}

.navbar .navbar-placeholder {
  transform: skewX(-12.56deg);
}

@media (min-width: 1024px) {
  .navbar a.navbar-item:hover {
    color: white !important;
    background: none;
    transition: color 0.2s 0.1s ease;
  }

  .navbar a.navbar-item:hover::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0%;
    height: 2.5rem;
    background: #e94a10;
    z-index: -10;
    transform: skewX(-12.56deg);
    -webkit-animation: slide 0.2s forwards;
    -webkit-animation-delay: 0.1s;
    animation: slide 0.2s forwards;
    animation-delay: 0.1s;
  }
}
@-webkit-keyframes slide {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}

@keyframes slide {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}
</style>
